import { Component, Injector, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { AdvancedTextHandlerService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/advanced-text-handler.service';
import { TextEditorSelectionElementBuilderService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-selection-element-builder.service';
import { AdvancedTextPanelJustifyService } from '../text-panel-services/advanced-text-panel-justify.service';
import { AdvancedTextPanelFontStyleService } from '../text-panel-services/advanced-text-panel-font-style.service';
import { AdvancedTextPanelListsService } from '../text-panel-services/advanced-text-panel-lists.service';
import { AdvancedTextPanelBufferService } from '../text-panel-services/advanced-text-panel-buffer.service';
import { AdvancedTextPanelHistoryService } from '../text-panel-services/advanced-text-panel-history.service';
import { AdvancedTextPanelRemoveFormatService } from '../text-panel-services/advanced-text-panel-remove-format.service';
import { AdvancedTextPanelMediaLinkService } from '../text-panel-services/advanced-text-panel-media-link.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { ITextPanelButton, ITextPanelButtonsGroup, TextPanelBase } from '../simple-text-panel/text-panel-base';
import { ObjectTypes } from '@fi-shared/types';
import {
    BACKSPACE,
    DELETE,
    DOWN_ARROW,
    END,
    HOME,
    INSERT,
    LEFT_ARROW,
    PAGE_DOWN,
    PAGE_UP,
    RIGHT_ARROW,
    UP_ARROW,
} from '@angular/cdk/keycodes';
import { takeUntil } from 'rxjs/operators';
import { isNull } from 'util';
import { Animations } from '@fi-shared/utils/animations';
import { fiDebounce, isDefined } from '@fi-shared/utils';
import { FiAdvancedTextPanelTableHelperService } from '../text-panel-services/advanced-text-panel-table-helper.service';
import { FiAIAssistanceHelperService } from '@fi-shared/fi-ai-assistance/fi-ai-assistance-helper.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { areColorsEqual } from '@fi-shared/utils/fi-color-compare';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { findClosest } from '@fi-shared/utils/closest';

enum FiTextColorStyle {
    BackgroundColor = 'backgroundColor',
    Color = 'color',
}

interface IFontSizeOption {
    label: string;
    menuValue: string;
    value: string;
}

@Component({
    selector: 'fi-advanced-text-panel',
    templateUrl: './advanced-text-panel.component.html',
    styleUrls: ['./advanced-text-panel.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fade],
    host: { class: 'fi-advanced-text-panel' },
    standalone: false,
})
export class AdvancedTextPanelComponent extends TextPanelBase implements OnInit, OnDestroy {
    @Input() objectType: ObjectTypes;
    @Input() isAIAssistanceHidden: boolean;
    btnGroups: ITextPanelButtonsGroup[] = [];
    btnGroups2: ITextPanelButtonsGroup[] = [];
    showMobileExtended: boolean = false;

    readonly fontSizes: IFontSizeOption[] = [
        { label: 'font_size.heading_1', menuValue: '24px', value: '36px' },
        { label: 'font_size.heading_2', menuValue: '22px', value: '28px' },
        { label: 'font_size.heading_3', menuValue: '18px', value: '24px' },
        { label: 'font_size.large_text', menuValue: '18px', value: '18px' },
        { label: 'font_size.medium_text', menuValue: '16px', value: '16px' },
        { label: 'font_size.small_text', menuValue: '14px', value: '14px' },
    ];

    readonly DefaultTextSizeValue: string = '16px';
    readonly DefaultTextSizeIndex: number = this.fontSizes.findIndex((size: IFontSizeOption) => {
        return size.menuValue === this.DefaultTextSizeValue;
    });
    selectedFontSizeIndex: number = this.DefaultTextSizeIndex;

    fontStyleAndOrderingListButtons: string[] = ['bold', 'underline', 'italic', 'bullet list', 'numbered list'];
    private readonly colorChangedClassName: string = 'custom-color';
    private readonly backgroundColorChangedClassName: string = 'custom-background-color';
    private lastChangedNode: HTMLElement;
    foreColor: string = '#000';
    backgroundColor: string = '#fff';
    private readonly defaultTextColor: string = '#000';
    private readonly defaultBackgroundColor: string = '#fff';
    private colorChangeSelection: Selection;
    private colorChangeSelectionRange: Range;
    private selectedLangId: number;
    private setColorSelectedDebounced = fiDebounce(() => this.setColorSelected(), 200);

    constructor(
        private renderer: Renderer2,
        private textEditorHistoryService: TextEditorHistoryService,
        private advancedTextHandlerService: AdvancedTextHandlerService,
        private textEditorSelectionElementBuilder: TextEditorSelectionElementBuilderService,
        private advancedTextPanelFontStyleService: AdvancedTextPanelFontStyleService,
        private advancedTextPanelListsService: AdvancedTextPanelListsService,
        private advancedTextPanelJustifyService: AdvancedTextPanelJustifyService,
        private advancedTextPanelBufferService: AdvancedTextPanelBufferService,
        private advancedTextPanelHistoryService: AdvancedTextPanelHistoryService,
        private advancedTextPanelRemoveFormatService: AdvancedTextPanelRemoveFormatService,
        private advancedTextPanelMediaLinkService: AdvancedTextPanelMediaLinkService,
        private advancedTextPanelTableHelperService: FiAdvancedTextPanelTableHelperService,
        private fiAiAssistanceHelperService: FiAIAssistanceHelperService,
        private fiCustomerDataService: FiCustomerDataService,
        private fiBrowserEventsService: FiBrowserEventsService,
        injector: Injector,
    ) {
        super(injector, undefined, true);

        const target_key_codes: number[] = [
            LEFT_ARROW,
            UP_ARROW,
            RIGHT_ARROW,
            DOWN_ARROW,
            INSERT,
            DELETE,
            BACKSPACE,
            PAGE_UP,
            PAGE_DOWN,
            HOME,
            END,
        ];
        this.advancedTextHandlerService.onKeyup$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((e: KeyboardEvent) => {
            if (target_key_codes.indexOf(e.keyCode) !== -1) {
                this.updateCursorPosition(e);
            }

            // https://chainformation.atlassian.net/browse/FI-4442
            // remove unused &zwnj;
            if (this.lastChangedNode && !this.lastChangedNode.innerText) {
                this.lastChangedNode.remove();
                this.lastChangedNode = undefined;
            }
        });
        this.advancedTextHandlerService
            .getCurrentContentLangId()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((langId: number) => (this.selectedLangId = langId));
    }

    ngOnInit() {
        this.setButtonGroups();
        this.handleAdvancedTextClick();
        this.setSelectedColorOnClickOrKeyUp();
    }
    private setSelectedColorOnClickOrKeyUp(): void {
        this.fiBrowserEventsService
            .getDocumentEventSubject$('mouseup')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.setColorSelectedDebounced());
        this.fiBrowserEventsService
            .getDocumentEventSubject$('keyup')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => this.setColorSelectedDebounced());
    }

    getCurrentFontSize(): string {
        return this.appTextService.get(this.fontSizes[this.selectedFontSizeIndex].label);
    }

    isFontSizeSelected(value: number): boolean {
        return this.selectedFontSizeIndex === value;
    }

    private handleAdvancedTextClick() {
        this.advancedTextHandlerService.onClick$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((event: MouseEvent) => {
            this.currentElement = event.target as HTMLElement;
            this.advancedTextPanelJustifyService.updateHtmlElementAlignment(
                getComputedStyle(event.target as HTMLElement).textAlign,
            );
            this.updateCursorPosition(event);
        });
    }

    private setButtonGroups() {
        if (/*this.fiCustomerDataService.isAIAssistanceDevCustomer() && */ !this.isAIAssistanceHidden) {
            this.btnGroups = [{ buttons: this.fiAiAssistanceHelperService.getPanelButtons() }];
        }

        this.btnGroups = [
            ...this.btnGroups,
            {
                buttons: this.advancedTextPanelHistoryService.getButtons(),
            },
            {
                buttons: this.advancedTextPanelFontStyleService.getButtons(
                    this.selectedButtonsData,
                    this.changeTextStyles.bind(this),
                ),
                class: 'mobile-basic',
            },
            {
                buttons: this.advancedTextPanelListsService.getButtons(),
                class: 'mobile-basic mobile-no-divider-extended',
            },
            {
                buttons: this.advancedTextPanelJustifyService.getButtons(this.selectedLangId),
                class: 'mobile-extended',
            },
            {
                buttons: this.advancedTextPanelBufferService.getButtons(),
            },
            {
                buttons: this.advancedTextPanelRemoveFormatService.getButtons(),
            },
        ];

        this.btnGroups2 = [
            {
                buttons: this.advancedTextPanelMediaLinkService.getButtons(this.objectType),
                class: 'mobile-basic mobile-no-divider-extended',
            },
        ];

        this.setIsDisabledPropButtons(this.btnGroups);
        this.setIsDisabledPropButtons(this.btnGroups2);
        //this.fiAiAssistanceHelperService.setIsAIAssistanceAvailable(true);
    }

    getButtonIconClass(buttonClass: string | (() => string)): string {
        return typeof buttonClass === 'string' ? buttonClass : buttonClass();
    }

    showMobileExtendedClick() {
        this.showMobileExtended = true;
    }

    buttonClick(button: ITextPanelButton, $event: MouseEvent) {
        if (
            /*CcmDetectDevices.isMobilePhone() && */ this.fontStyleAndOrderingListButtons.indexOf(
                button.title.toLowerCase(),
            ) != -1
        )
            this.advancedTextHandlerService.getCurrent().focus();

        button.callback();
        $event.stopPropagation();
        this.triggerChange();
    }

    triggerChange() {
        const current: HTMLElement = this.advancedTextHandlerService.getCurrent();
        if (current) current.dispatchEvent(new Event('change'));
    }

    isMediaElement(element: HTMLElement): boolean {
        return element.matches('img') || element.matches('video');
    }

    updateCursorPosition(event: MouseEvent | KeyboardEvent) {
        if (!this.isMediaElement(event.target as HTMLElement)) {
            // note: setTimeout is here to get correct anchor node, without it if user selects content and clicks on
            // element -> contenteditable div is returned
            setTimeout(() => {
                const selection: Selection = window.getSelection();

                if (selection && selection.anchorNode) {
                    const currentNode: Node =
                        selection.anchorNode.nodeType === Node.TEXT_NODE
                            ? selection.anchorNode.parentNode
                            : selection.anchorNode;

                    this.updateSizesByStyleNode(currentNode as Element);
                }
            });
        }
    }

    updateSizesByStyleNode(node: Element) {
        if (node.tagName.toLowerCase() === 'div' && node.children.length === 1 && !node.matches('[contenteditable]'))
            node = node.children[0];

        // in case if whole content was selected and removed we need to clear contenteditable div to remove font-size
        if (node.matches('[contenteditable]') && (node.innerHTML === '<br>' || node.innerHTML === '<div><br></div>')) {
            node.innerHTML = '';
            const selection: Selection = window.getSelection();
            selection.removeAllRanges();
            const range: Range = new Range();
            range.setStart(node, 0);
            range.setEnd(node, 0);
            selection.addRange(range);
        }

        const currentSize: string = window.getComputedStyle(node).fontSize;
        let sizeMatch: RegExpMatchArray = currentSize.match(/(^.+)(px|pt)$/),
            size: string;
        if (sizeMatch.length === 3) {
            size = Math.round(parseFloat(sizeMatch[1])) + sizeMatch[2];
        }

        let newSizeIndex: number = this.fontSizes.findIndex((fontSize: IFontSizeOption) => fontSize.value === size);

        if (newSizeIndex === -1) {
            newSizeIndex = this.getCorrectFontSizeIndex(Math.round(parseFloat(sizeMatch[1])));
        }

        if (newSizeIndex !== this.selectedFontSizeIndex) {
            this.selectedFontSizeIndex = newSizeIndex;
        }
    }

    getCorrectFontSizeIndex(size: number): number {
        const fontSizesArrayCopy: IFontSizeOption[] = [...this.fontSizes];
        const availableSizes: number[] = fontSizesArrayCopy
            .reverse()
            .map((fontSize: IFontSizeOption) => {
                return Math.round(parseFloat(fontSize.value.match(/(^.+)(px|pt)$/)[1]));
            })
            .filter((fontSize: number) => fontSize > size);

        if (availableSizes.length) {
            return this.fontSizes.findIndex((fontSize: IFontSizeOption) => {
                return Math.round(parseFloat(fontSize.value.match(/(^.+)(px|pt)$/)[1])) === availableSizes[0];
            });
        }
        return 0;
    }

    sizeChange(newValue: number) {
        const sel: Selection = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.textEditorSelectionElementBuilder.getCurrentRange());
        this.execChange((span: Node, isFocusPrevented?: boolean) => {
            this.renderer.setStyle(span, 'fontSize', this.fontSizes[newValue].value);
            this.selectedFontSizeIndex = newValue;
            if (!isFocusPrevented) this.focusChangedNode();
        }, 'font-size');
    }

    private focusChangedNode() {
        setTimeout(() => {
            const selection: Selection = window.getSelection();
            const range = new Range();
            range.setStart(selection.focusNode, 0);
            range.setEndAfter(selection.focusNode);
            selection.removeAllRanges();
            selection.addRange(range);
        });
    }

    // https://snipplr.com/view/10912/get-html-of-selection
    private getSelectionHTML(): string {
        let userSelection;
        if (this.colorChangeSelection || window.getSelection) {
            // W3C Ranges
            userSelection = this.colorChangeSelection || window.getSelection();
            // Get the range:
            let range: Range;
            if (userSelection.getRangeAt) range = this.colorChangeSelectionRange || userSelection.getRangeAt(0);
            else {
                range = document.createRange();
                range.setStart(userSelection.anchorNode, userSelection.anchorOffset);
                range.setEnd(userSelection.focusNode, userSelection.focusOffset);
            }
            // And the HTML:
            const clonedSelection = range.cloneContents();
            const span = document.createElement('span');
            span.appendChild(clonedSelection);
            return span.innerHTML;
        }
        if ((<any>document).selection) {
            // Explorer selection, return the HTML
            userSelection = this.colorChangeSelectionRange || (<any>document).selection.createRange();
            return userSelection.htmlText;
        }

        return '';
    }

    private clearChildrenStyles(node: Node, styleName: string) {
        node.childNodes.forEach((childNode) => {
            if (childNode.nodeType !== Node.TEXT_NODE) {
                this.renderer.removeStyle(childNode, styleName);
                this.renderer.removeClass(childNode, this.colorChangedClassName);
                this.renderer.removeClass(childNode, this.backgroundColorChangedClassName);
                this.clearChildrenStyles(childNode, styleName);
            }
        });
    }

    private execChangeOnTable(
        func: (arg: Node, isFocusPrevented?: boolean) => void,
        styleName: string,
        sel: Selection,
    ) {
        const range: Range = sel.getRangeAt(0);
        const startNode: Node = range.startContainer;
        const selectedCells: HTMLTableCellElement[] = this.advancedTextPanelTableHelperService
            .getSelectedCells(this.advancedTextPanelTableHelperService.getTableElementFromSelectionNode(startNode), sel)
            .filter((selectedCell) => isDefined(selectedCell));

        if (!startNode.isEqualNode(range.endContainer) && selectedCells.length !== 1) {
            selectedCells.forEach((selectedCell: HTMLTableCellElement) => {
                const spanElement: HTMLElement = document.createElement('SPAN');
                const spanElementContainer: HTMLElement = document.createElement('SPAN');
                spanElement.innerHTML = selectedCell.innerHTML;
                if (!spanElement.innerHTML.length) {
                    spanElement.innerHTML = '&nbsp;';
                }
                func(spanElement, true);
                spanElementContainer.appendChild(spanElement as Node);
                this.clearChildrenStyles(spanElement, styleName);
                selectedCell.innerHTML = spanElementContainer.innerHTML;
                sel.removeAllRanges();
            });
            this.triggerChange();
            return;
        }

        this.execChangeOnCommonItem(func, styleName, sel);
    }

    execChangeOnCommonItem(func: (arg: Node, isFocusPrevented?: boolean) => void, styleName: string, sel: Selection) {
        if (sel.rangeCount) {
            // Creates a new element, and insert the selected text with the chosen font inside
            const e: HTMLElement = document.createElement('SPAN');
            //e.style = 'font-family:' + font.value + ';';
            const selectionHTML: string = this.getSelectionHTML() || '&zwnj;';
            //if (selectionHTML.length !== 0) {
            e.innerHTML = selectionHTML;
            func(e);

            // https://developer.mozilla.org/en-US/docs/Web/API/Selection/getRangeAt
            const range: Range = this.colorChangeSelectionRange || sel.getRangeAt(0);
            range.deleteContents(); // Deletes selected text…
            range.insertNode(e); // … and inserts the new element at its place

            const parentElement: HTMLElement = e.parentNode as HTMLElement;
            let targetNode: HTMLElement = e;
            if (parentElement.nodeName === 'SPAN' && parentElement.textContent === e.textContent) {
                // prevent having nested spans
                // instead of comparing textContent we can check if parentElement.childNodes contains current element
                // and all empty text childnodes
                parentElement.innerHTML = selectionHTML;
                func(parentElement);
                targetNode = parentElement;
            }

            this.clearChildrenStyles(targetNode, styleName);

            // select node content
            sel.removeAllRanges();
            const newRange: Range = new Range();
            newRange.selectNodeContents(targetNode);
            if (this.colorChangeSelectionRange) {
                this.colorChangeSelectionRange = newRange;
            }
            sel.addRange(newRange);
            this.lastChangedNode = targetNode;

            /*} else {
                // zwnj is inserted to have an opportunity to use different font sizes in one row
                e.innerHTML = '&zwnj;';
                const range = sel.getRangeAt(0);
                range.insertNode(e);
                func(e);
            }*/
            this.triggerChange();
        }
    }

    private execChange(func: (arg: Node, isFocusPrevented?: boolean) => void, styleName: string) {
        this.textEditorHistoryService.saveState();
        const sel: Selection = this.colorChangeSelection || window.getSelection(); // Gets selection

        const anchorNode = sel.anchorNode;
        const focusNode = sel.focusNode;
        if (this.advancedTextPanelTableHelperService.isAnyOfAnchorOrFocusNodesTableNodes(anchorNode, focusNode)) {
            const focusOffset = sel.focusOffset;
            const anchorOffset = sel.anchorOffset;
            const tableElement = this.advancedTextPanelTableHelperService.getTableElementFromSelectionNode(focusNode);
            if (!tableElement) {
                return;
            }
            const tableCells = tableElement.querySelectorAll('td, th');

            if (this.advancedTextPanelTableHelperService.isAnchorNodeTableItem(anchorNode, focusNode)) {
                const tableSectionStartNode = this.advancedTextPanelTableHelperService.getTableSelectionStartNode(
                    sel,
                    tableCells,
                    anchorNode,
                );
                this.execChangeOnTable(
                    func,
                    styleName,
                    this.advancedTextPanelTableHelperService.selectNodesBetween(
                        tableSectionStartNode as Node,
                        focusNode,
                    ),
                );
                const restSelection = this.advancedTextPanelTableHelperService.selectNodesBetween(
                    focusNode,
                    focusNode,
                    0,
                    focusOffset,
                );
                this.execChangeOnCommonItem(func, styleName, restSelection);
            } else if (this.advancedTextPanelTableHelperService.isFocusNodeTableItem(anchorNode, focusNode)) {
                this.execChangeOnTable(
                    func,
                    styleName,
                    this.advancedTextPanelTableHelperService.selectNodesBetween(tableCells.item(0), focusNode),
                );
                const restSelection = this.advancedTextPanelTableHelperService.selectNodesBetween(
                    anchorNode,
                    tableElement as Node,
                    anchorOffset,
                );
                this.execChangeOnCommonItem(func, styleName, restSelection);
            }
            return;
        }

        if (this.advancedTextPanelTableHelperService.getTableElementFromSelectionNode(anchorNode)) {
            this.execChangeOnTable(func, styleName, sel);
            return;
        }

        this.execChangeOnCommonItem(func, styleName, sel);
    }

    foreColorChange(newColor: string) {
        if (!this.foreColor) {
            this.foreColor = newColor;
            return;
        }

        if (newColor) {
            this.foreColor = newColor;
            this.setColorStyleWithInputPreserve(newColor, FiTextColorStyle.Color);
        }
    }

    backgroundColorChange(newColor: string) {
        if (!this.backgroundColor) {
            this.backgroundColor = newColor;
            return;
        }

        if (newColor) {
            this.backgroundColor = newColor;
            this.setColorStyleWithInputPreserve(newColor, FiTextColorStyle.BackgroundColor);
        }
    }

    //https://chainformation.atlassian.net/browse/FI-3778
    private setColorStyleWithInputPreserve(newColor: string, style: string) {
        let activeElement: HTMLInputElement;
        let inputValue: string;

        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
            activeElement = <HTMLInputElement>document.activeElement;
            inputValue = activeElement.value;
        }

        this.execChange((span) => {
            this.renderer.addClass(document.body, 'transparent-selection');
            this.renderer.setStyle(span, style, newColor);
            this.renderer.addClass(
                span,
                style === FiTextColorStyle.BackgroundColor
                    ? this.backgroundColorChangedClassName
                    : this.colorChangedClassName,
            );
            // https://chainformation.atlassian.net/browse/FI-4179
            // If you select more than one row, highlight color is not applied, seems to only be working on single rows.
            if (style === FiTextColorStyle.BackgroundColor) {
                this.renderer.setStyle(span, 'display', 'inline-block');
            }
        }, style);
        if (activeElement) {
            activeElement.focus();
            setTimeout(() => (activeElement.value = inputValue));
        }
    }

    private setSelectionRangeOnColorPickerOpen(): void {
        const selection = window.getSelection();
        if (selection?.rangeCount > 0) {
            if (!findClosest(selection.anchorNode, '.color-picker')) {
                this.colorChangeSelection = selection;
                this.colorChangeSelectionRange = selection.getRangeAt(0);
            }
        } else {
            this.resetColorChangeSelection();
        }
    }

    private resetColorChangeSelection(): void {
        this.colorChangeSelection = undefined;
        this.colorChangeSelectionRange = undefined;
    }

    isFontColorPickerShown$ = new BehaviorSubject<boolean>(false);
    isBackgroundColorPickerShown$ = new BehaviorSubject<boolean>(false);
    setColorSelected() {
        this.setCurrentColorStyle(FiTextColorStyle.Color);
        this.setCurrentColorStyle(FiTextColorStyle.BackgroundColor);
        this.cdr.markForCheck();
    }
    private setCurrentColorStyle(colorStyle: FiTextColorStyle) {
        this.setSelectionRangeOnColorPickerOpen();
        const selectionParentElement: HTMLElement = this.getSelectionParentElement(colorStyle);
        if (colorStyle === FiTextColorStyle.Color) {
            this.foreColor = isNull(selectionParentElement)
                ? this.defaultTextColor
                : selectionParentElement.style.color;
        } else if (colorStyle === FiTextColorStyle.BackgroundColor) {
            this.backgroundColor = isNull(selectionParentElement)
                ? this.defaultBackgroundColor
                : selectionParentElement.style.backgroundColor;
        }
    }

    protected foreColorToggleChange(isPickerShown: boolean): void {
        this.isFontColorPickerShown$.next(isPickerShown);
        if (isPickerShown) {
            this.setCurrentColorStyle(FiTextColorStyle.Color);
            return;
        }
        this.onColorPickerClose();
    }

    protected backgroundColorToggleChange(isPickerShown: boolean): void {
        this.isBackgroundColorPickerShown$.next(isPickerShown);
        if (isPickerShown) {
            this.setCurrentColorStyle(FiTextColorStyle.BackgroundColor);
            return;
        }
        this.onColorPickerClose();
    }

    private onColorPickerClose() {
        this.resetColorChangeSelection();
        this.renderer.removeClass(document.body, 'transparent-selection');
    }

    private getSelectionParentElement(style: FiTextColorStyle): HTMLElement {
        // todo https://stackoverflow.com/questions/35281283/safari-getselection-not-working
        if (this.colorChangeSelection?.rangeCount > 0)
            return this.getParentElementFromRange().closest(
                `.${
                    style === FiTextColorStyle.Color ? this.colorChangedClassName : this.backgroundColorChangedClassName
                }`,
            );

        return null;
    }

    private getParentElementFromRange(): HTMLElement {
        if (this.isTextNodeSelection()) return this.colorChangeSelectionRange.startContainer.parentElement;

        return this.colorChangeSelectionRange.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
            ? (this.colorChangeSelectionRange.commonAncestorContainer as HTMLElement)
            : this.colorChangeSelectionRange.commonAncestorContainer.parentElement;
    }

    private isTextNodeSelection(): boolean {
        const isStartTextNode: boolean = this.colorChangeSelectionRange.startContainer.nodeType === Node.TEXT_NODE;
        const isCommonAncestorContainerTextNode: boolean =
            this.colorChangeSelectionRange.commonAncestorContainer.nodeType === Node.TEXT_NODE;
        const isCommonAncestorContainerEditor: boolean =
            !isCommonAncestorContainerTextNode &&
            (this.colorChangeSelectionRange.commonAncestorContainer as HTMLElement).classList.contains('text-editor');

        return isStartTextNode && isCommonAncestorContainerEditor;
    }

    protected isTextColorNotDefault(): boolean {
        return !areColorsEqual(this.foreColor, this.defaultTextColor);
    }
    protected isBackgroundColorNotDefault(): boolean {
        return !areColorsEqual(this.backgroundColor, this.defaultBackgroundColor);
    }
    protected isSelectedFontSizeNotDefault(): boolean {
        return this.DefaultTextSizeIndex !== this.selectedFontSizeIndex;
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy() {
        super.ngOnDestroy();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
        this.onColorPickerClose();
        this.fiAiAssistanceHelperService.setIsAIAssistanceVisible(false);
    }
}
